import React from 'react';
import styles from './LegalModal.module.css';

interface TermsConditionsProps {
  onClose: () => void;
}

const TermsConditions: React.FC<TermsConditionsProps> = ({ onClose }) => {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          <i className="fas fa-times" />
        </button>
        
        <div className={styles.modalBody}>
          <h1>Terms & Conditions</h1>
          <p className={styles.lastUpdated}><strong>Last Updated:</strong> January 9, 2025</p>

          <h2>Agreement to Terms</h2>
          <p>
            By accessing danwhitewebsolutions.com or engaging our services, you agree to be bound by 
            these Terms and Conditions. If you disagree with any part of these terms, you may not use 
            our services.
          </p>

          <h2>About Our Services</h2>
          <p>
            Dan White Web Solutions provides web design and development services to small businesses 
            and sole traders across Australia, including:
          </p>
          <ul>
            <li>Static SPA (Single Page Application) websites</li>
            <li>Custom website development</li>
            <li>Website hosting setup assistance</li>
          </ul>

          <h2>Pricing & Payment</h2>
          
          <h3>Static SPA Package</h3>
          <ul>
            <li><strong>Regular Price:</strong> $2000 + GST</li>
            <li><strong>2025 Launch Special:</strong> $1500 + GST (valid for onboarding completed before December 31, 2025)</li>
          </ul>

          <h3>Custom Website Package</h3>
          <ul>
            <li>Starting from $4000 + GST</li>
            <li>Final price quoted after consultation based on project requirements</li>
          </ul>

          <h3>Payment Terms</h3>
          <p><strong>Static SPA Package ($1500 + GST):</strong></p>
          <ul>
            <li>Deposit: $500 + GST ($550 total) required to commence work</li>
            <li>Balance: $1000 + GST ($1100 total) due upon project completion</li>
          </ul>
          
          <p><strong>Custom Website Package ($4000+ + GST):</strong></p>
          <ul>
            <li>Deposit: 50% + GST required to commence work</li>
            <li>Balance: 50% + GST due upon project completion</li>
          </ul>

          <p>
            Payment accepted via bank transfer or other agreed methods. Prices are in Australian Dollars (AUD).
          </p>

          <h3>GST</h3>
          <p>
            All prices quoted include Australian Goods and Services Tax (GST). A tax invoice will be 
            provided upon payment.
          </p>

          <h2>Project Process</h2>
          
          <h3>Timeline</h3>
          <ul>
            <li>Static SPA websites typically completed within 2-4 weeks</li>
            <li>Custom websites: timeline discussed during consultation</li>
            <li>Timelines are estimates and may vary based on project complexity and client responsiveness</li>
          </ul>

          <h3>Client Responsibilities</h3>
          <p>You agree to:</p>
          <ul>
            <li>Provide all required content, images, and information in a timely manner</li>
            <li>Review and provide feedback on drafts within 7 days</li>
            <li>Respond to communications within a reasonable timeframe</li>
            <li>Provide access to necessary accounts (domain, hosting, etc.)</li>
          </ul>

          <h3>Revisions</h3>
          <ul>
            <li>Static SPA: Up to 2 rounds of revisions included</li>
            <li>Custom Website: Revisions as agreed in project scope</li>
            <li>Additional revisions may incur extra charges</li>
          </ul>

          <h2>Intellectual Property</h2>
          
          <h3>Client Content</h3>
          <p>You retain ownership of all content, images, and materials you provide.</p>

          <h3>Website Code & Design</h3>
          <p>Upon full payment:</p>
          <ul>
            <li>You receive a license to use the completed website</li>
            <li>You own the final website design specific to your business</li>
            <li>Dan White Web Solutions retains the right to reuse code structures and templates for other clients</li>
            <li>We may display your website in our portfolio unless otherwise agreed</li>
          </ul>

          <h2>Hosting & Domain</h2>
          <ul>
            <li>Domain registration and hosting are not included unless specifically stated</li>
            <li>We can assist with setup using your choice of hosting provider</li>
            <li>Static SPA websites are typically hosted on GitHub Pages (free)</li>
            <li>You are responsible for ongoing hosting fees (if applicable)</li>
          </ul>

          <h2>Cancellation & Refunds</h2>
          
          <h3>Before Work Commences</h3>
          <p>Full refund of deposit if cancelled before work begins.</p>

          <h3>After Work Commences</h3>
          <ul>
            <li>Deposit is non-refundable once design work has started</li>
            <li>If you cancel mid-project, you will be charged for work completed to date</li>
            <li>You will receive all completed work upon payment</li>
          </ul>

          <h3>Project Delays</h3>
          <p>
            If the project is delayed due to client non-responsiveness for more than 30 days, we reserve 
            the right to cancel the project and retain the deposit.
          </p>

          <h2>Warranties & Limitations</h2>
          
          <h3>What We Provide</h3>
          <ul>
            <li>Professional web design services</li>
            <li>Websites that are responsive and work on modern browsers</li>
            <li>Basic SEO setup (meta tags, structured data)</li>
          </ul>

          <h3>What We Don't Guarantee</h3>
          <ul>
            <li>Specific search engine rankings</li>
            <li>Specific business outcomes or revenue</li>
            <li>Compatibility with outdated browsers (e.g., Internet Explorer)</li>
            <li>Ongoing security for client-managed content</li>
          </ul>

          <h3>Limitation of Liability</h3>
          <p>
            To the extent permitted by law, Dan White Web Solutions shall not be liable for any indirect, 
            incidental, or consequential damages arising from the use of our services. Our total liability 
            shall not exceed the amount paid for services.
          </p>

          <h2>Maintenance & Support</h2>
          <ul>
            <li>Static SPA websites require minimal to no maintenance</li>
            <li>Minor content updates may be requested within 30 days of project completion at no charge</li>
            <li>Ongoing maintenance packages available upon request (separate fee)</li>
          </ul>

          <h2>Australian Consumer Law</h2>
          <p>
            Nothing in these terms excludes, restricts, or modifies any consumer rights under the Australian 
            Consumer Law that cannot be excluded, restricted, or modified by agreement.
          </p>

          <h2>Governing Law</h2>
          <p>
            These Terms are governed by the laws of New South Wales, Australia. Any disputes shall be subject 
            to the jurisdiction of the courts of New South Wales.
          </p>

          <h2>Contact</h2>
          <p>For questions about these Terms and Conditions:</p>
          <div className={styles.contactBox}>
            <p><strong>Dan White Web Solutions</strong></p>
            <p>ABN: 96 770 057 153</p>
            <p>Email: danwhitewebsolutions@outlook.com</p>
            <p>Phone: 0432 430 287</p>
            <p>Address: 14 Sky Place, Bellingen NSW 2454</p>
          </div>

          <p className={styles.agreement}>
            By using our services, you acknowledge that you have read, understood, and agree to be bound 
            by these Terms and Conditions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;